// src/components/admin/TicketManagement.js
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  Switch,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addTicket,
  deleteTicket,
  getTickets,
  updateTicket,
} from "../../../../store/actions/ticketsActions";

const TicketManagement = ({ provider }) => {
  const dispatch = useDispatch();
  const ticketsState = useSelector((state) => state.tickets || {});
  const ticketsFromStore = ticketsState.list || [];
  const seasonsState = useSelector((state) => state.seasons || {});
  const seasonsFromStore = seasonsState.list || [];
  const matchesState = useSelector((state) => state.matches || {});
  const matchesFromStore = matchesState.list || [];

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [openView, setOpenView] = useState(false);

  const [ticketForm, setTicketForm] = useState({
    provider: provider?._id || "",
    ticketType: "single", // single | season
    category: "Normal", // VIP | Normal | Guest
    season: null,
    match: null,
    price: 0,
    quantity: 0,
    nfcCode: "",
    status: "active",
  });

  const ticketsPerPage = 8;

  useEffect(() => {
    if (provider?._id) {
      setLoading(true);
      dispatch(getTickets(provider._id));
      dispatch({ type: "GET_SEASONS_IF_NOT" });
      dispatch({ type: "GET_MATCHES_IF_NOT" });
    }
  }, [provider, dispatch]);

  useEffect(() => setLoading(false), [ticketsFromStore]);
  useEffect(() => setPage(1), [search]);

  const filtered = ticketsFromStore.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (t.ticketId || "").toLowerCase().includes(q) ||
      (t.match?.title || "").toLowerCase().includes(q) ||
      (t.season?.name || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filtered.length / ticketsPerPage) || 1;
  const current = filtered.slice(
    (page - 1) * ticketsPerPage,
    page * ticketsPerPage
  );

  const openAddDialog = () => {
    setEditingTicket(null);
    setTicketForm({
      provider: provider?._id || "",
      ticketType: "single",
      category: "Normal",
      season: null,
      match: null,
      price: 0,
      quantity: 0,
      nfcCode: "",
      status: "active",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (t) => {
    setEditingTicket(t);
    setTicketForm({
      provider: t.provider?._id || t.provider,
      ticketType: t.ticketType,
      category: t.category || "Normal",
      season: t.season?._id || t.season || null,
      match: t.match?._id || t.match || null,
      price: t.price,
      quantity: t.quantity,
      nfcCode: t.nfcCode || "",
      status: t.status,
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (ticketForm.ticketType === "season" && !ticketForm.season) {
      return alert("Season ticket requires season selection.");
    }
    if (ticketForm.ticketType === "single" && !ticketForm.match) {
      return alert("Single ticket requires match selection.");
    }

    const payload = { ...ticketForm, provider: provider._id };

    if (editingTicket?._id) {
      await dispatch(updateTicket({ id: editingTicket._id, payload }));
    } else {
      await dispatch(addTicket(payload));
    }

    dispatch(getTickets(provider._id));
    setOpenDialog(false);
    setEditingTicket(null);
  };

  const handleDelete = (t) => {
    if (!window.confirm(`Delete this ticket? ${t.ticketId || t._id}`)) return;
    dispatch(deleteTicket({ ticketId: t._id, providerId: provider._id }));
  };

  return (
    <Box sx={{ maxWidth: "99%", mx: "auto" }}>
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          size="small"
          placeholder="Search tickets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
          }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
        >
          Add Ticket
        </Button>
      </Box>

      {/* ✅ Tickets Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : current.length === 0 ? (
        <Typography>No tickets found</Typography>
      ) : (
        <Stack spacing={2}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Match / Season</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {current.map((t) => (
                  <TableRow key={t._id}>
                    <TableCell>{t.ticketId || t._id}</TableCell>
                    <TableCell>{t.ticketType}</TableCell>
                    <TableCell>{t.category || "-"}</TableCell>
                    <TableCell>
                      {t.ticketType === "season"
                        ? t.season?.name || "N/A"
                        : t.match?.title || "N/A"}
                    </TableCell>
                    <TableCell>{t.price}</TableCell>
                    <TableCell>{t.quantity}</TableCell>
                    <TableCell>{t.status}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => setOpenView(t)} color="info">
                        <ViewIcon />
                      </IconButton>
                      <IconButton onClick={() => openEditDialog(t)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(t)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
      )}

      {/* ✅ Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTicket ? "Edit Ticket" : "Add Ticket"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel id="ticket-type-label">Ticket Type</InputLabel>
            <Select
              labelId="ticket-type-label"
              value={ticketForm.ticketType}
              label="Ticket Type"
              onChange={(e) =>
                setTicketForm({ ...ticketForm, ticketType: e.target.value })
              }
            >
              <MenuItem value="single">Single Match Ticket</MenuItem>
              <MenuItem value="season">Season Ticket</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={ticketForm.category}
              label="Category"
              onChange={(e) =>
                setTicketForm({ ...ticketForm, category: e.target.value })
              }
            >
              <MenuItem value="VIP">VIP</MenuItem>
              <MenuItem value="Normal">Normal</MenuItem>
              <MenuItem value="Guest">Guest</MenuItem>
              <MenuItem value="Premium">Premium</MenuItem>
            </Select>
          </FormControl>

          {ticketForm.ticketType === "single" ? (
            <FormControl fullWidth>
              <InputLabel id="match-select-label">Select Match</InputLabel>
              <Select
                labelId="match-select-label"
                value={ticketForm.match || ""}
                label="Select Match"
                onChange={(e) =>
                  setTicketForm({ ...ticketForm, match: e.target.value })
                }
              >
                <MenuItem value="">Select Match</MenuItem>
                {matchesFromStore.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.title} — {moment(m.matchDate).format("DD-MM-YYYY")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <InputLabel id="season-select-label">Select Season</InputLabel>
              <Select
                labelId="season-select-label"
                value={ticketForm.season || ""}
                label="Select Season"
                onChange={(e) =>
                  setTicketForm({ ...ticketForm, season: e.target.value })
                }
              >
                <MenuItem value="">Select Season</MenuItem>
                {seasonsFromStore.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Price"
              type="number"
              value={ticketForm.price}
              onChange={(e) =>
                setTicketForm({ ...ticketForm, price: e.target.value })
              }
            />
            <TextField
              label="Quantity"
              type="number"
              value={ticketForm.quantity}
              onChange={(e) =>
                setTicketForm({ ...ticketForm, quantity: e.target.value })
              }
            />
            <TextField
              label="NFC Code (optional)"
              value={ticketForm.nfcCode}
              onChange={(e) =>
                setTicketForm({ ...ticketForm, nfcCode: e.target.value })
              }
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={ticketForm.status === "active"}
                onChange={(e) =>
                  setTicketForm({
                    ...ticketForm,
                    status: e.target.checked ? "active" : "inactive",
                  })
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ View Ticket */}
      <Dialog open={Boolean(openView)} onClose={() => setOpenView(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ticket Details</DialogTitle>
        <DialogContent>
          {openView && (
            <Box>
              <Typography>Ticket ID: {openView.ticketId || openView._id}</Typography>
              <Typography>Type: {openView.ticketType}</Typography>
              <Typography>Category: {openView.category}</Typography>
              <Typography>
                Match/Season:{" "}
                {openView.ticketType === "season"
                  ? openView.season?.name
                  : openView.match?.title}
              </Typography>
              <Typography>Price: {openView.price}</Typography>
              <Typography>Quantity: {openView.quantity}</Typography>
              <Typography>Status: {openView.status}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TicketManagement;
